import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth, homePathFor } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";

import Home from "./pages/Home";
import Collection from "./pages/Collection";
import CollectionItem from "./pages/CollectionItem";
import { Login, Register, ForgotPassword, VerifyEmail } from "./pages/Auth";
import { MemberDashboard, NewSubmission, MySubmissions } from "./pages/Member";
import SubmissionDetail from "./pages/SubmissionDetail";
import { StaffDashboard, SubmissionQueue, TasksPage } from "./pages/Staff";
import { ValidatorDashboard } from "./pages/Validator";
import { AdminDashboard, UserManagement, AdminSettings, AuditLog } from "./pages/Admin";
import { Notifications, InfoGatherPage, ThankYou, AboutPage, ParticipatePage, ContactPage } from "./pages/Misc";

// Role-tuple constants — extracted so JSX props keep stable references.
const R_MEMBER = ["member"];
const R_STAFF = ["staff", "admin"];
const R_VALIDATOR = ["validator"];
const R_ADMIN = ["admin"];

function DashboardRedirect() {
  const { user } = useAuth();
  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={homePathFor(user)} replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors closeButton toastOptions={{ style: { fontFamily: "Inter, system-ui, sans-serif" } }} />
        <Routes>
          {/* Public */}
          <Route path="/" element={<Home />} />
          <Route path="/collection" element={<Collection />} />
          <Route path="/collection/:id" element={<CollectionItem />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/participate" element={<ParticipatePage />} />
          <Route path="/participate/info" element={<InfoGatherPage />} />
          <Route path="/participate/thank-you" element={<ThankYou />} />
          <Route path="/contact" element={<ContactPage />} />

          {/* Auth */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />

          {/* Shared authenticated */}
          <Route path="/dashboard" element={<DashboardRedirect />} />
          <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />

          {/* Member */}
          <Route path="/member" element={<ProtectedRoute roles={R_MEMBER}><MemberDashboard /></ProtectedRoute>} />
          <Route path="/member/submissions" element={<ProtectedRoute roles={R_MEMBER}><MySubmissions /></ProtectedRoute>} />
          <Route path="/member/submissions/new" element={<ProtectedRoute roles={R_MEMBER}><NewSubmission /></ProtectedRoute>} />
          <Route path="/member/submissions/:id" element={<ProtectedRoute roles={R_MEMBER}><SubmissionDetail /></ProtectedRoute>} />

          {/* Staff */}
          <Route path="/staff" element={<ProtectedRoute roles={R_STAFF}><StaffDashboard /></ProtectedRoute>} />
          <Route path="/staff/submissions" element={<ProtectedRoute roles={R_STAFF}><SubmissionQueue /></ProtectedRoute>} />
          <Route path="/staff/submissions/:id" element={<ProtectedRoute roles={R_STAFF}><SubmissionDetail /></ProtectedRoute>} />
          <Route path="/staff/tasks" element={<ProtectedRoute roles={R_STAFF}><TasksPage /></ProtectedRoute>} />

          {/* Validator */}
          <Route path="/validator" element={<ProtectedRoute roles={R_VALIDATOR}><ValidatorDashboard /></ProtectedRoute>} />
          <Route path="/validator/submissions/:id" element={<ProtectedRoute roles={R_VALIDATOR}><SubmissionDetail /></ProtectedRoute>} />

          {/* Admin */}
          <Route path="/admin" element={<ProtectedRoute roles={R_ADMIN}><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute roles={R_ADMIN}><UserManagement /></ProtectedRoute>} />
          <Route path="/admin/settings" element={<ProtectedRoute roles={R_ADMIN}><AdminSettings /></ProtectedRoute>} />
          <Route path="/admin/audit" element={<ProtectedRoute roles={R_ADMIN}><AuditLog /></ProtectedRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
